import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import request from 'lib/request';
import {
  clonedUnitsAtom,
  tcSelectedDevAreaAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import type { AreaUsageItem, AreaUsagesResponse } from 'types/area-usages';
import type { CreatedAreaGeometry } from 'types/api-responses';
import { developmentAreaFromUsages } from './created-unit-id';

/** `POST /queries/area_geometries/usages` — resolves an area to its unit. */
const USAGES_PATH = '/queries/area_geometries/usages';

/**
 * Turns a just-saved draft into the real unit it now is.
 *
 * Saving CREATEs the unit server-side, but the builder keeps reading as a draft
 * because `builderScopedUnitAtom` reports `unitId: null` for anything in the
 * clone registry. Every read-only behaviour — the hidden overlap opt-in, the
 * disabled Save, read-only surveys, disarmed map-pick, live Generate LTD /
 * Update Geometries / Rotate — derives from that one id, so re-pointing the
 * selection flips all of them at once and no gate needs to know about saving.
 *
 * The selection is also where the unit's NAME is displayed from: the header
 * navigator renders `selected.developmentAreaName` (and the builder's title
 * reads the same value through `builderScopedUnitAtom`), so this is what makes
 * the main page and the builder agree on the saved unit's name instead of both
 * showing "<source> (copy)".
 *
 * The real `development_areas.id` is fetched rather than taken from the
 * development-areas listing, because that listing can legitimately omit a
 * brand-new unit (see `developmentAreaFromUsages`).
 *
 * @return {Function} Adopts the created unit and returns it, so a caller that
 *   needs the real development-area id (the force-keep unit action) can use the
 *   same lookup rather than repeating it. Null when nothing was adopted.
 */
export const useAdoptSavedUnit = (): ((
  created: CreatedAreaGeometry
) => Promise<AreaUsageItem | null>) => {
  const selected = useAtomValue(tcSelectedDevAreaAtom);
  const setClones = useSetAtom(clonedUnitsAtom);
  const setSelected = useSetAtom(tcSelectedDevAreaAtom);
  const cloneId = selected?.developmentAreaId;

  return useCallback(
    async (created: CreatedAreaGeometry): Promise<AreaUsageItem | null> => {
      if (!created?.id) return null;

      const response = await request.post<AreaUsagesResponse, unknown>(
        USAGES_PATH,
        { areaGeometryId: created.id },
        new AbortController()
      );
      if (response.type !== 'success') return null;

      const unit = developmentAreaFromUsages(response.data);
      // No routed record reported — leave the draft alone. Save is already
      // locked by `savedDraftAtom`, so nothing can be created twice.
      if (!unit) return null;

      // Both writes belong to one commit. Dropping the clone alone would leave
      // `builderScopedUnitAtom` handing out the clone's SYNTHETIC NEGATIVE id
      // instead of null — worse than the draft state, because every id-keyed read
      // would then fire against it.
      if (cloneId !== undefined) {
        setClones((prev) => prev.filter((clone) => clone.id !== cloneId));
      }
      setSelected((prev) => ({
        ...(prev ?? {}),
        developmentAreaId: unit.id,
        // The server's own name for the unit, which is what the navigator
        // indexes by and what both headers display.
        developmentAreaName: unit.label || created.name,
        // `sourceDevelopmentAreaId` MUST go. Four reads resolve
        // `sourceDevelopmentAreaId ?? developmentAreaId` (the gun barrel, both
        // slot fetches, the boundary read), so leaving it would keep the saved
        // unit resolving the unit it was copied from, for good. Undefined reads
        // as absent to all four.
        sourceDevelopmentAreaId: undefined,
      }));
      // Keeps `ring`, `azimuthDeg` and `crossSectionEndPoints`: the gun barrel
      // and both slot fetches key on the cross-section line, and the boundary
      // re-reads itself from the server for the new id anyway.
      return unit;
    },
    [cloneId, setClones, setSelected]
  );
};
